// The composer, and everything behind one plus. Lifted from the Andon app's
// `Composer`; GIFs and reactions stay, the follow-up task row does not.
//
// Gloved hands photograph faster than they type, so the camera keeps its own
// shortcut on the bar and everything else goes behind a single `+`: a photo or
// video from the gallery, record an audio clip, record a video clip, upload a
// file, add a GIF.
//
// The emoji key is IN the field rather than beside it, which is where every
// messaging app puts it and is not a coincidence: the bar has room for two
// icons at 44px next to a text box on a 375px screen, and both of those are
// spoken for by the camera and the microphone — the two things a gloved hand
// reaches for first. An emoji is a character, so it belongs with the
// characters.
//
// ATTACHMENTS LAND IN THE THREAD AS MESSAGES.
//
// Which means they are timestamped, attributed and pushed to followers — not
// filed in a separate evidence tab that nobody scrolls to. That is the whole
// reason this is a composer and not an upload button.
//
// PICKING IS NOT SENDING. A pick starts its own upload and lands in the tray
// below the box; Send posts ONE message carrying every id, so four photos and
// the sentence about them are one bubble. See hooks/useAttachments.ts.
//
// WHAT THE LIFT CHANGED. Dictation is the shop floor's own `useDictation`, in
// the app's language, behind the consent the quality form and the raise
// already ask for once and remember. Every word on screen is a TR key.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AlertDetailVM, AlertEventVM, ThreadMessageVM } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { Icon, type IconName } from '@/components/ui/Icon';
import { MAX_STAGED, useAttachments, type Staged } from '@/hooks/useAttachments';
import { useDictation } from '@/hooks/useDictation';
import { useRecorder } from '@/hooks/useRecorder';
import { useT } from '@/i18n/useT';
import { captureBlob, captureFile, clipLength, fileSize, type CaptureWords } from '@/lib/capture';
import { insertEmoji } from '@/lib/emoji';
import { downscaleToJpeg } from '@/lib/image';
import { pendingMessage } from '@/lib/optimistic';
import { canSend, stillUploading } from '@/lib/staging';
import { andonKeys } from '@/queries/andon';
import { useStore } from '@/store';
import { EmojiPicker } from './EmojiPicker';
import { GifPicker } from './GifPicker';
import { MentionList, useMentions } from './MentionPicker';
import { PickerRow } from './PickerRow';
import { VideoRecorder } from './VideoRecorder';

/**
 * The tallest the message box is allowed to get, in pixels.
 *
 * Five lines and a bit at the field's 20px line height, plus its own padding —
 * enough for the longest thing anybody types into an andon in one go, and not
 * so much that the thread it is a reply to disappears behind it on a 6" phone
 * with the keyboard up. Past this it scrolls.
 */
const BOX_MAX = 126;

export function Composer({
  alertId,
  quickReplies,
  gifSeed = 'reaction',
}: {
  alertId: number;
  quickReplies?: React.ReactNode;
  /** What the GIF picker searches before anybody types. See lib/gifSeed.ts. */
  gifSeed?: string;
}) {
  const t = useT();
  const qc = useQueryClient();
  const showError = useStore((s) => s.showError);
  const [draft, setDraft] = useState('');
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * Files still climbing the uplink after their message has gone.
   *
   * Shown under the box rather than left to the thread, because the thread now
   * holds the REAL message — the words went the moment Send was tapped — and
   * the only thing left to say is that something is still on its way to it.
   */
  const [trailing, setTrailing] = useState(0);
  const box = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const photoInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const recorderWords = useMemo(
    () => ({
      cannotRecord: t.recCannotT,
      micRefused: t.recMicRefusedT,
      cameraRefused: t.recCameraRefusedT,
      couldNotStartAudio: t.recStartAudioFailedT,
      couldNotStartVideo: t.recStartVideoFailedT,
    }),
    [t],
  );
  const captureWords = useMemo<CaptureWords>(
    () => ({ unreadable: t.captureUnreadableT, is: t.captureIsT, limit: t.captureLimitT, thatClip: t.captureThatClipT }),
    [t],
  );
  const audio = useRecorder('audio', recorderWords);
  const video = useRecorder('video', recorderWords);
  /**
   * A VOICE CLIP CAN ARRIVE WITH ITS WORDS.
   *
   * A clip is the fastest thing a gloved hand can send and the slowest thing on
   * the thread to read: nobody scrubs a 40-second recording to find out whether
   * it needs them, so voice notes get listened to last. So the browser's own
   * recogniser runs alongside the recorder and what it hears lands in the box
   * as the caption. The clip is still the record; the text is a draft,
   * editable before Send — recognition is wrong often enough with machinery
   * running that treating it as final would put invented words on a record.
   *
   * GATED ON CONSENT, and that is not a formality. On Android Chrome the audio
   * goes to Google's servers to be recognised, where recording a clip sends it
   * only to Odoo. The picker asks once, with that sentence, and remembers —
   * the same consent the quality form and the raise ask for.
   */
  const language = useStore((s) => s.language);
  const dictation = useDictation(language);
  const consent = useStore((s) => s.speechConsent);
  const setConsent = useStore((s) => s.setSpeechConsent);
  const files = useAttachments(alertId, t.composerWouldNotUploadT);
  const [gifOpen, setGifOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // The row is drawn from the session's feature flag, which is false whenever
  // the server has no key.
  const gifsEnabled = useStore((s) => s.andonGifs);
  // The name on the in-flight bubble. The employee behind the PIN, or the
  // account — the same pair the server uses to decide `mine`, so the
  // optimistic bubble and the real one that replaces it say the same thing.
  const operator = useStore((s) => s.operator);
  const userUid = useStore((s) => s.userUid);

  /**
   * Pick a GIF: the server fetches it and hands back an ordinary attachment.
   *
   * Staged like everything else, so it lands in the tray, goes with whatever
   * else is there, and carries the caption in the same bubble. The bytes are
   * COPIED into Odoo rather than linked — a thread that renders somebody
   * else's CDN stops being a record the moment that URL rots.
   */
  const pickGif = async (gifId: string, title: string) => {
    setGifOpen(false);
    if (files.full) {
      showError(t.composerTrayFullT);
      return;
    }
    setBusy(true);
    try {
      const out = await andonApi.gifAttach(alertId, gifId);
      files.adopt({ id: out.attachment.id, name: title ? `${title}.gif` : out.attachment.name, kind: 'photo', bytes: out.attachment.bytes });
      keepFocus();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Put the caret back in the box.
   *
   * On a phone this is what keeps the keyboard up between messages, and losing
   * it after every send is the difference between a conversation and a form
   * you fill in repeatedly. Deferred a frame because React has not committed
   * the cleared value yet when `onSuccess` runs.
   */
  const keepFocus = () => requestAnimationFrame(() => box.current?.focus());

  const refresh = () => void qc.invalidateQueries({ queryKey: andonKeys.alert(alertId) });

  const mentions = useMentions(draft, caret, setDraft);
  const { suggestions, pick, pendingCaret } = mentions;

  /**
   * Put an emoji where the caret is, not on the end.
   *
   * Same contract as picking a mention, and it reuses the same caret handoff:
   * `pendingCaret` is applied in a LAYOUT effect rather than a frame later,
   * because until React has committed the longer string the input still holds
   * the old value and `setSelectionRange` clamps to it.
   */
  const addEmoji = (emoji: string) => {
    const out = insertEmoji(draft, caret, emoji);
    setDraft(out.text);
    pendingCaret.current = out.caret;
  };

  useLayoutEffect(() => {
    const at = pendingCaret.current;
    if (at == null) return;
    pendingCaret.current = null;
    const el = box.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(at, at);
    setCaret(at);
  }, [draft, pendingCaret]);

  /**
   * THE BOX GROWS WITH WHAT IS IN IT, UP TO A POINT.
   *
   * It wraps, and the field grows a line at a time to `BOX_MAX`, and past that
   * it scrolls VERTICALLY with the caret's line in view — the behaviour of
   * every messaging app anybody using this has ever used. The cap exists
   * because the composer sits at the bottom of the screen with the thread
   * above it, and a box that grew without limit would eat the conversation it
   * is a reply to.
   *
   * Measured rather than counted: `scrollHeight` after a reset to `auto` is
   * the only number that accounts for wrapping and whatever the browser's font
   * metrics actually are. Reset first or the box can only ever get taller.
   */
  const fitBox = useCallback(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, BOX_MAX)}px`;
  }, []);

  useLayoutEffect(fitBox, [draft, fitBox]);

  // And again when the field itself changes width — a tablet turned on its
  // side rewraps every line in the box.
  useEffect(() => {
    window.addEventListener('resize', fitBox);
    return () => window.removeEventListener('resize', fitBox);
  }, [fitBox]);
  const fail = (err: unknown) => showError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : t.composerWouldNotSendT);

  /** The thread payload, as react-query holds it. */
  type AlertQuery = { alert: AlertDetailVM; thread: ThreadMessageVM[]; events: AlertEventVM[] };
  const key = andonKeys.alert(alertId);

  /**
   * SEND PUTS THE BUBBLE UP FIRST, then tells the server.
   *
   * Waiting for the post AND for the refetch behind it before the words
   * appeared was, on a factory uplink, most of a second of a screen that
   * looked like it did not hear the tap. Nobody reads that as care; they read
   * it as broken, and they tap Send again.
   *
   * The rollback is the price of that and it is paid in full: on a failure the
   * bubble goes, the words come back into the box with the caret in them, the
   * mentions come back, and the tray comes back with the files still uploaded.
   * See lib/optimistic.ts for why a message is the only thing drawn ahead of
   * the server.
   */
  const send = useMutation({
    /**
     * Post at once with whatever has landed, then catch up the rest.
     *
     * THE SECOND HALF IS NOT ROLLED BACK ON FAILURE, and cannot be. By the
     * time a late upload fails the message is already on the thread and
     * already pushed to the followers — so this reports the loss and leaves
     * the message, which is the only honest option.
     */
    mutationFn: async (body: string) => {
      const waiting = stillUploading(files.staged).map((f) => f.key);
      const posted = await andonApi.message(alertId, body, mentions.logins(body), files.ids);
      if (waiting.length === 0 || posted.messageId == null) return posted;

      // THE WORDS HAVE GONE, so the thread should say so rather than holding a
      // dimmed "Sending…" bubble for the minutes a video takes.
      setTrailing(waiting.length);
      refresh();

      const landed = await files.settle(waiting);
      setTrailing(0);
      const ids = landed.map((l) => l.id).filter((id): id is number => id != null);
      if (ids.length > 0) {
        await andonApi
          .attachToMessage(alertId, posted.messageId, ids)
          // The bytes are in Odoo and the message is on the thread; what
          // failed is the staple between them. Worth saying, not worth
          // pretending the message did not go.
          .catch(() => showError(t.composerAttachFailedT));
      }
      const lost = landed.filter((l) => l.id == null);
      if (lost.length > 0) {
        showError(lost.length === 1 ? (lost[0]!.error ?? t.composerOneLostT) : `${lost.length} ${t.composerManyLostT}`);
      }
      return posted;
    },

    onMutate: async (body: string) => {
      // Otherwise a poll already in flight lands after this and paints the
      // bubble straight back out again.
      await qc.cancelQueries({ queryKey: key });
      const sent = { draft, picked: mentions.picked, staged: files.staged };

      qc.setQueryData<AlertQuery>(key, (old) =>
        old == null
          ? old
          : {
              ...old,
              thread: [
                ...old.thread,
                pendingMessage({
                  body,
                  author: operator?.name ?? t.youT,
                  authorEmployeeId: operator?.id ?? null,
                  authorUid: operator ? null : userUid,
                  files: sent.staged,
                  at: Date.now(),
                  uploading: stillUploading(sent.staged).length,
                }),
              ],
            },
      );

      // Emptied here rather than in `onSuccess`: the point of the whole
      // arrangement is that the box is ready for the next sentence before the
      // first one has landed.
      setDraft('');
      mentions.reset();
      setCaret(0);
      files.clear();
      keepFocus();
      return sent;
    },

    onError: (err, _body, sent) => {
      // Every trace of the optimism, undone. A message that failed to send is
      // one somebody wants to retry, not retype.
      qc.setQueryData<AlertQuery>(key, (old) => (old == null ? old : { ...old, thread: old.thread.filter((m) => !m.pending) }));
      if (sent) {
        setDraft(sent.draft);
        mentions.restore(sent.picked);
        setCaret(sent.draft.length);
        files.restore(sent.staged);
      }
      setTrailing(0);
      fail(err);
      keepFocus();
    },

    // Both ways. On success this is what swaps the pending bubble for the real
    // one; on failure it re-reads a thread the rollback has already put right.
    onSettled: () => refresh(),
  });

  /**
   * Stage one file and let its upload run.
   *
   * NOT awaited. The picker closes and the tray row appears immediately, with
   * the upload proceeding behind it — which is what lets somebody pick four
   * photos in four taps instead of waiting out each one.
   */
  const stage = (kind: 'photo' | 'audio' | 'video' | 'file', captured: Awaited<ReturnType<typeof captureFile>>, blob?: Blob) => {
    setPicker(false);
    if (files.full) {
      showError(t.composerTrayFullT);
      return;
    }
    void files.add(kind, captured, blob);
    keepFocus();
  };

  const onPhoto = async (file: File) => {
    setBusy(true);
    try {
      // Downscaled before it ever enters state — a raw phone JPEG held as a
      // base64 string is enough to kill the WebView on the Zebra tablets.
      const data = await downscaleToJpeg(file);
      stage('photo', { name: file.name || 'photo.jpg', data, size: file.size, seconds: null }, file);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File) => {
    setBusy(true);
    try {
      const captured = await captureFile(file, captureWords);
      // The KIND is what the picker row meant, so a video picked from the
      // gallery gets a video's preview rather than a file card. The server
      // sniffs the bytes regardless — this only chooses a renderer.
      const kind = file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : file.type.startsWith('image/') ? 'photo' : 'file';
      stage(kind, captured, file);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /** Start the microphone, and the recogniser with it when it is allowed to. */
  const startVoice = () => {
    if (consent && dictation.supported) dictation.start();
    void audio.start();
  };

  const stopRecording = async () => {
    // Read BEFORE stopping, because `dictation.stop()` ends the session and the
    // interim words of the last phrase are the ones most likely to be the
    // point of the whole clip.
    const heard = dictation.listening ? dictation.transcript.trim() : '';
    if (dictation.listening) dictation.stop();
    const out = await audio.stop();
    if (!out) return;
    try {
      const captured = await captureBlob(out.blob, out.name, out.seconds, captureWords);
      stage('audio', captured, out.blob);
      // Into the box rather than sent: the transcript is a draft and the person
      // who spoke is the only one who can tell whether the recogniser heard
      // "Biesse" or "peace". Appended, so a caption typed before recording
      // survives.
      if (heard) setDraft((prev) => (prev.trim() ? `${prev.trim()} ${heard}` : heard).slice(0, 2000));
    } catch (err) {
      fail(err);
    }
  };

  /**
   * A clip the operator has watched back and accepted.
   *
   * Separate from `stopRecording` because the two moments are genuinely
   * different: that one stops the tracks and takes what came out, this one
   * takes a blob that was stopped some seconds ago and has since been
   * reviewed.
   */
  const stageClip = async (clip: { blob: Blob; name: string; seconds: number }) => {
    try {
      const captured = await captureBlob(clip.blob, clip.name, clip.seconds, captureWords);
      stage('video', captured, clip.blob);
    } catch (err) {
      fail(err);
    }
  };

  // The 46px strip is AUDIO ONLY. Video takes the screen — see VideoRecorder.
  const recording = audio.recording;

  /** Discard the clip and the words with it — nothing left behind either way. */
  const cancelRecording = () => {
    if (dictation.listening) dictation.stop();
    audio.cancel();
  };

  const submit = () => {
    const body = draft.trim();
    // WORDS OR FILES. A photo of the empty rack with nothing typed under it is
    // the commonest thing a gloved hand sends.
    if (send.isPending || !canSend(body, files.staged)) return;
    send.mutate(body);
  };

  return (
    <div style={{ flex: 'none', padding: '12px 20px calc(16px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--line)' }}>
      {/* The state changes, as things you SAY. Above the composer rather than
          in an action bar, because tapping one posts into the thread AND flips
          the state — so the escalation everyone sees has a visible cause. */}
      {quickReplies}

      {/* The @ list, above the box because the box is at the bottom of the
          screen and a phone keyboard is under it. Rendered only while a
          mention is being typed — it is not a control anybody opens. */}
      {!recording && <MentionList suggestions={suggestions} onPick={pick} above label={t.mentionLabelThreadT} />}

      {/* What is waiting to go. Above the bar because it is part of the
          message being written, not a separate upload panel. */}
      {files.staged.length > 0 && !recording && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'thin' }}>
          {files.staged.map((file) => (
            <StagedTile key={file.key} file={file} onRemove={() => files.forget(file.key)} onRetry={() => files.retry(file.key)} />
          ))}
        </div>
      )}

      {/* THE VIEWFINDER, over everything. Rendered from the composer rather
          than from a screen because the recorder hook lives here and the clip
          lands in this tray. */}
      {video.recording && (
        <VideoRecorder
          recorder={video}
          onSend={(clip) => void stageClip(clip)}
          onDiscard={() => {
            /* The recorder has already released its tracks. Nothing staged, so
               there is nothing to undo. */
          }}
        />
      )}

      {recording ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, height: 46, padding: '0 12px', borderRadius: 15, background: 'rgba(255,90,95,.12)', border: '1px solid var(--danger)' }}>
          <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--danger)', animation: 'ipRec 1.2s infinite', flex: 'none' }} />
          <span style={{ font: '600 12px Manrope,sans-serif', color: 'var(--ink)' }}>{t.composerRecordingAudioT}</span>
          <span className="numeric" style={{ flex: 1, font: "700 13px 'Space Grotesk',sans-serif", color: 'var(--ink)' }}>
            {clipLength(audio.seconds)}
          </span>
          <Pill label={t.discardT} onClick={cancelRecording} />
          <Pill label={t.sendT} primary onClick={() => void stopRecording()} />
        </div>
      ) : (
        // Bottom-aligned rather than centred: the box grows UPWARD as lines are
        // added, and the keys either side must stay on the floor of the bar
        // where the thumbs left them rather than sliding up the field.
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button
            type="button"
            className="press"
            aria-label={t.composerAddAriaT}
            aria-expanded={picker}
            disabled={busy}
            onClick={() => setPicker((v) => !v)}
            style={{
              width: 46,
              height: 46,
              borderRadius: 15,
              background: picker ? 'var(--ink)' : 'var(--surface)',
              border: `1px solid ${picker ? 'var(--ink)' : 'var(--line)'}`,
              color: picker ? 'var(--bg)' : 'var(--ink)',
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              transform: picker ? 'rotate(45deg)' : undefined,
              transition: 'transform .15s ease',
            }}
          >
            <Icon name="plus" size={21} strokeWidth={1.8} />
          </button>
          {/* The field is a box with two things in it, so the emoji key sits
              inside the same rounded border as the text it writes. A MINIMUM
              height, not a height: one line looks exactly as it did, and the
              box is free to grow to `BOX_MAX` under it. */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', minHeight: 46, borderRadius: 15, background: 'var(--bg)', border: '1px solid var(--line)' }}>
            <textarea
              ref={box}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setCaret(e.target.selectionStart ?? e.target.value.length);
              }}
              // Both, and neither is enough alone: `onSelect` catches a click or
              // an arrow key moving the caret into an existing @name, `onChange`
              // catches typing.
              onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onKeyDown={(e) => {
                if (suggestions.length > 0) {
                  // While the picker is open Enter takes the top suggestion
                  // rather than sending — the alternative is a message that goes
                  // out as "@be" to nobody.
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    pick(suggestions[0]!);
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    const end = draft.length;
                    box.current?.setSelectionRange(end, end);
                    setCaret(end);
                    return;
                  }
                }
                // ENTER STILL SENDS. It is a chat box at the bottom of a
                // thread and that is the only thing Enter can mean there.
                // Shift+Enter breaks the line for anybody on a keyboard.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={t.composerPlaceholderT}
              aria-label={t.composerPlaceholderT}
              enterKeyHint="send"
              style={{
                flex: 1,
                minWidth: 0,
                height: 44,
                maxHeight: BOX_MAX,
                padding: '12px 4px 12px 14px',
                background: 'transparent',
                border: 'none',
                color: 'var(--ink)',
                font: '500 13px/20px Manrope,sans-serif',
                outline: 'none',
                resize: 'none',
                overflowX: 'hidden',
                overflowY: 'auto',
                boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              className="press"
              aria-label={t.composerAddEmojiT}
              aria-haspopup="dialog"
              // `onMouseDown` prevented for the same reason the mention list
              // does it: a click lands after the input has lost focus, which on
              // a phone drops the keyboard and scrolls the thread.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setEmojiOpen(true)}
              style={{ width: 38, height: 44, display: 'grid', placeItems: 'center', color: 'var(--sub)', flex: 'none', background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              <Icon name="smile" size={19} strokeWidth={1.8} />
            </button>
          </div>
          {draft.trim() || files.staged.length > 0 ? (
            <button
              type="button"
              className="press"
              aria-label={t.sendT}
              disabled={send.isPending || !canSend(draft, files.staged)}
              // THE KEYBOARD MUST NOT BLINK BETWEEN MESSAGES. A tap on this
              // button takes focus off the input, which drops the phone
              // keyboard; `keepFocus` then puts it back and the keyboard
              // slides up again. Preventing the mousedown default keeps the
              // input focused, so there is nothing to restore.
              onMouseDown={(e) => e.preventDefault()}
              onClick={submit}
              style={{ width: 46, height: 46, borderRadius: 15, background: 'var(--accent)', color: 'var(--accentInk)', display: 'grid', placeItems: 'center', flex: 'none' }}
            >
              <Icon name="send" size={19} />
            </button>
          ) : (
            <>
              {/* The camera keeps its own key. It is the single fastest thing a
                  gloved hand can do, and burying it one tap deeper than the
                  keyboard would be the wrong way round on this floor. */}
              <IconKey label={t.takePhoto} icon="camera" disabled={busy} onClick={() => photoInput.current?.click()} />
              {audio.supported && <IconKey label={t.composerRecordVoiceT} icon="mic" disabled={busy} onClick={startVoice} />}
            </>
          )}
        </div>
      )}

      {/* What the recogniser is hearing, live. Shown so somebody can tell it
          is working — and, more usefully, tell that it is NOT. */}
      {recording && dictation.listening && (
        <p aria-live="polite" style={{ margin: '8px 2px 0', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {dictation.transcript.trim() || t.composerListeningT}
        </p>
      )}

      {picker && !recording && (
        <div style={{ marginTop: 10, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--line)', overflow: 'hidden' }}>
          <PickerRow icon="gallery" label={t.composerGalleryT} disabled={busy} onClick={() => fileInput.current?.click()} />
          {audio.supported && (
            <PickerRow
              icon="mic"
              label={t.composerRecordAudioT}
              // Says whether the words will come with it, so nobody has to
              // discover the difference by sending one and reading the thread.
              {...(consent && dictation.supported ? { hint: t.composerTranscribesT } : {})}
              disabled={busy}
              onClick={startVoice}
            />
          )}
          {video.supported && <PickerRow icon="video" label={t.composerRecordVideoT} disabled={busy} onClick={() => void video.start()} />}
          <PickerRow icon="upload" label={t.composerUploadT} hint={t.composerUploadHintT} disabled={busy} onClick={() => fileInput.current?.click()} />
          {/* Only when a Giphy key is configured. A row that opens and then
              says the feature is not set up is worse than one never offered. */}
          {gifsEnabled && (
            <PickerRow
              icon="gif"
              label={t.gifAddT}
              hint={t.composerGifHintT}
              disabled={busy}
              onClick={() => {
                setPicker(false);
                setGifOpen(true);
              }}
            />
          )}

          {/* THE OFFER, WITH THE DISCLOSURE ON IT, ONCE. Nothing is blocked
              either way: a clip records the moment the mic is tapped,
              transcript or no transcript. */}
          {dictation.supported && audio.supported && !consent && (
            <button
              type="button"
              className="press-row"
              onClick={() => {
                setConsent(true);
                setPicker(false);
              }}
              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 16, padding: '12px 18px 14px', textAlign: 'left', color: 'var(--ink)' }}
            >
              <Icon name="keyboard" size={20} strokeWidth={1.8} style={{ flex: 'none', marginTop: 2 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', font: '500 14px Manrope,sans-serif' }}>{t.composerConsentTitleT}</span>
                <span style={{ display: 'block', font: '500 10.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>{t.voiceConsentAsk}</span>
              </span>
            </button>
          )}
        </div>
      )}

      {trailing > 0 && (
        <p aria-live="polite" style={{ margin: '8px 2px 0', font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
          {trailing === 1 ? t.composerTrailingOneT : `${trailing} ${t.composerTrailingManyT}`}
        </p>
      )}

      {(audio.error || video.error || dictation.error) && (
        <p role="status" style={{ margin: '8px 2px 0', font: '500 11px Manrope,sans-serif', color: 'var(--danger)' }}>
          {/* The recorder first: a clip that failed to record is a lost message,
              where a transcript that failed is a caption somebody can type. */}
          {audio.error ?? video.error ?? dictation.error}
        </p>
      )}

      <EmojiPicker
        open={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        // The sheet stays OPEN. An emoji is punctuation as often as it is a
        // word, and "🎉🎉🎉" would otherwise be three round trips through a
        // sheet that closes each time.
        onPick={addEmoji}
        title={t.composerAddEmojiT}
      />

      {gifsEnabled && <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onPick={(id, title) => void pickGif(id, title)} busy={busy} seed={gifSeed} />}

      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onPhoto(file);
          e.target.value = '';
        }}
      />
      <input
        ref={fileInput}
        type="file"
        accept="image/*,video/*,audio/*,application/pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * One file waiting to go, while it uploads.
 *
 * A tile rather than a row: four photos side by side read as "four photos",
 * where four full-width rows read as a list somebody has to check. The
 * thumbnail comes from the bytes already in the browser, so it appears on the
 * tap rather than after a round trip.
 *
 * THE THREE STATES ARE VISUALLY DISTINCT. Uploading dims and runs a bar;
 * failed goes red and keeps its remove button, because the point of staging
 * is that one bad file does not cost the other three; done is simply the
 * picture.
 *
 * A FAILED TILE IS A RETRY BUTTON. The commonest failure here is not a bad
 * file — it is thirty bad seconds of uplink. Tapping it sends it again; the ×
 * in the corner still removes it.
 */
function StagedTile({ file, onRemove, onRetry }: { file: Staged; onRemove: () => void; onRetry: () => void }) {
  const t = useT();
  const uploading = file.id == null && file.error == null;
  const failed = file.error != null;
  const label = file.seconds != null ? clipLength(file.seconds) : fileSize(file.bytes);

  return (
    <div
      // A `div` with a role rather than a `button`, because the remove control
      // lives inside it and a button inside a button is invalid HTML.
      {...(failed
        ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-label': `${file.name} ${t.composerTapRetryT}`,
            onClick: onRetry,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRetry();
              }
            },
          }
        : {})}
      style={{
        position: 'relative',
        width: 76,
        height: 76,
        flex: 'none',
        borderRadius: 13,
        overflow: 'hidden',
        background: 'var(--bg)',
        border: `1px solid ${failed ? 'var(--danger)' : 'var(--line)'}`,
        opacity: uploading ? 0.6 : 1,
        cursor: failed ? 'pointer' : undefined,
      }}
    >
      {file.preview && file.kind === 'photo' ? (
        <img src={file.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : file.preview && file.kind === 'video' ? (
        // `muted` and `playsInline` so iOS renders a poster frame instead of a
        // grey box, and never starts playing in a tray.
        <video src={file.preview} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'var(--sub)' }}>
          <Icon name={file.kind === 'audio' ? 'mic' : file.kind === 'video' ? 'video' : 'upload'} size={22} />
        </span>
      )}

      {/* The name is not on the tile. At 76px it would be three characters and
          an ellipsis; the size or the duration is the fact somebody actually
          checks before sending. */}
      <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '8px 5px 3px', background: 'linear-gradient(transparent, rgba(0,0,0,.72))', color: '#fff', font: "700 9.5px 'Space Grotesk',sans-serif", textAlign: 'center', textTransform: 'uppercase' }}>
        {failed ? t.composerRetryT : uploading ? t.composerSendingT : label}
      </span>

      {uploading && <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, height: 3, width: '40%', background: 'var(--accent)', animation: 'ipSweepX 1.1s linear infinite' }} />}

      <button
        type="button"
        aria-label={`${t.removeT} ${file.name}`}
        title={failed ? (file.error ?? undefined) : file.name}
        // Stopped here, or it would land on the tile's retry underneath and the
        // × would send the file again on its way out.
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: 8, border: 'none', background: 'rgba(0,0,0,.62)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0 }}
      >
        <Icon name="cross" size={13} />
      </button>
    </div>
  );
}

function IconKey({ label, icon, disabled, onClick }: { label: string; icon: IconName; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{ width: 44, height: 46, display: 'grid', placeItems: 'center', color: 'var(--sub)', flex: 'none', opacity: disabled ? 0.55 : 1 }}
    >
      <Icon name={icon} size={21} strokeWidth={1.8} />
    </button>
  );
}

function Pill({ label, primary, onClick }: { label: string; primary?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 13px',
        borderRadius: 10,
        background: primary ? 'var(--accent)' : 'var(--surface)',
        border: primary ? 'none' : '1px solid var(--line)',
        color: primary ? 'var(--accentInk)' : 'var(--ink)',
        font: '600 11.5px Manrope,sans-serif',
        flex: 'none',
      }}
    >
      {label}
    </button>
  );
}
